import { useCallback, useEffect, useState } from 'react';
import { ParcoursAffaireVue } from './parcours-affaire';
import { FilAffaire } from './messages-affaire';
import { SignaturesAffaire } from './signatures-affaire';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LE POSTE D'INSTRUCTION — une affaire, ouverte, instruite de bout en bout
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Fondateur, 24/09/2026 : « le bureau existe pour tout le monde, le poste
 * d'instruction est pour instruire UNE affaire ».
 *
 * ⚠️ CE QUI SÉPARE LE POSTE DU BUREAU N'EST PAS LA MISE EN PAGE, C'EST L'OBJET.
 * `Bureau({ modeCode, … })` ne reçoit AUCUNE affaire : il monte `MesAffaires` et
 * `PlanDeTravail`, c'est-à-dire la file — « par quoi je commence ». Le poste
 * reçoit UNE référence et ne la quitte pas. Deux écrans, deux questions.
 *
 * ⚠️⚠️ CE FICHIER N'EST PAS UN SEPTIÈME ÉCRAN, C'EST UN ASSEMBLAGE.
 * `PROMPT_POSTE_INSTRUCTION.md` §0 bis l'exige : six écrans font déjà des
 * morceaux du poste, et en ajouter un septième aggrave le problème au lieu de le
 * résoudre. Les trois onglets servis ci-dessous montent des composants qui
 * existaient déjà et qui prennent tous `reference` — rien n'est réécrit ici.
 * Une seconde implémentation du parcours, du fil ou des signatures divergerait
 * au premier correctif appliqué à une seule.
 *
 * ⚠️ DEUX ONGLETS NE SONT PAS SERVIS, ET ILS LE DISENT.
 * La maquette en demande cinq ; « Dossier » (la liasse) n'a aujourd'hui ni
 * écran ni route, et « Fraude » a six modules au service mais aucune surface.
 * Un onglet vide se lirait « rien à signaler » — sur un registre foncier, c'est
 * le contresens qui fait valider un dossier qu'on n'a pas regardé. Chacun
 * annonce donc ce qui lui manque, avec la mesure et l'item du catalogue.
 * Même règle que le volet Impact de la fiche parcelle : « aucun acte » et
 * « aucun impact » ne se ressemblent que dans un panneau muet.
 */

const ONGLETS = [
  { cle: 'instruction', libelle: 'Instruction' },
  { cle: 'messages',    libelle: 'Messages' },
  { cle: 'dossier',     libelle: 'Dossier' },
  { cle: 'fraude',      libelle: 'Fraude' },
  { cle: 'preuve',      libelle: 'Preuve' },
] as const;

type CleOnglet = (typeof ONGLETS)[number]['cle'];

export function PosteInstruction({ reference, onFermer, onChange }: {
  reference: string;
  onFermer?: () => void;
  onChange?: () => void;
}) {
  const [onglet, setOnglet] = useState<CleOnglet>('instruction');

  /**
   * ⚠️ LE CLAVIER EST DANS LA MAQUETTE, ET IL A UNE RAISON DE MÉTIER : un agent
   * qui instruit passe la journée sur cet écran. `1`–`5` posent l'onglet, `esc`
   * ferme. On ne capte PAS la frappe quand elle va dans un champ, sinon taper
   * « 3 » dans un motif changerait d'onglet en perdant la saisie.
   */
  const auClavier = useCallback((e: KeyboardEvent) => {
    const cible = e.target as HTMLElement | null;
    const balise = cible?.tagName;
    if (balise === 'INPUT' || balise === 'TEXTAREA' || balise === 'SELECT'
        || cible?.isContentEditable) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;

    if (e.key === 'Escape' && onFermer) { onFermer(); return; }
    const rang = Number(e.key);
    if (Number.isInteger(rang) && rang >= 1 && rang <= ONGLETS.length) {
      setOnglet(ONGLETS[rang - 1]!.cle);
    }
  }, [onFermer]);

  useEffect(() => {
    window.addEventListener('keydown', auClavier);
    return () => window.removeEventListener('keydown', auClavier);
  }, [auClavier]);

  return (
    <section className="poste-instruction">
      <header className="poste-tete">
        <div>
          <h2>Poste d'instruction</h2>
          {/* ⚠️ La référence est le SEUL état affiché ici. Le type de procédure
              et l'étape courante viennent de `/affaires/:ref/parcours`, que
              l'onglet Instruction charge déjà : les relire ici ferait deux
              requêtes pour un même fait, et la seconde pourrait le contredire. */}
          <p className="mention">Affaire {reference}</p>
        </div>
        {onFermer && (
          <button type="button" className="bouton discret" onClick={onFermer}>
            Fermer <span className="mention">esc</span>
          </button>
        )}
      </header>

      <nav className="onglets" aria-label="Sections du poste d'instruction">
        {ONGLETS.map((o, i) => (
          <button key={o.cle} type="button"
                  className={onglet === o.cle ? 'actif' : ''}
                  aria-current={onglet === o.cle ? 'page' : undefined}
                  onClick={() => setOnglet(o.cle)}>
            {o.libelle} <span className="mention">{i + 1}</span>
          </button>
        ))}
      </nav>

      {/* ⚠️ MONTÉ SEULEMENT QUAND L'ONGLET EST ACTIF. Chacun de ces trois
          composants appelle le service à son montage ; les monter tous les trois
          ferait trois requêtes pour un agent qui n'en regarde qu'une. */}
      {onglet === 'instruction' && <ParcoursAffaireVue reference={reference} onChange={onChange} />}
      {onglet === 'messages'    && <FilAffaire reference={reference} />}
      {onglet === 'preuve'      && <OngletPreuve reference={reference} />}
      {onglet === 'dossier'     && <OngletDossier />}
      {onglet === 'fraude'      && <OngletFraude />}
    </section>
  );
}

/**
 * PREUVE — ce qui rend l'affaire opposable à un tiers.
 *
 * ⚠️ LE DÉFAUT MESURÉ SE DIT ICI, IL NE S'IMPRIME PAS AILLEURS. Le 24/09/2026 :
 * `doc.document` porte 194 lignes et `doc.scellement` en porte **1**. Une pièce
 * non scellée s'affiche et s'imprime très bien ; simplement, rien ne permet à un
 * tiers de constater qu'une page n'a pas été substituée après coup. Taire ce
 * rapport ferait de cet onglet une garantie qui n'en est pas une.
 */
function OngletPreuve({ reference }: { reference: string }) {
  return (
    <>
      <SignaturesAffaire reference={reference} />
      <section className="avertissement">
        <strong>Le scellement n'est pas généralisé.</strong>
        <p className="mention">
          Mesuré le 24/09/2026 en production : 194 documents, <strong>1 scellement</strong>.
          Les signatures et les jetons de vérification existent ; le sceau, non.
          Une liasse dont les pièces ne sont pas scellées s'imprime et ne prouve rien —
          c'est le lot L7 du chantier, pas un réglage d'écran.
        </p>
      </section>
    </>
  );
}

/**
 * DOSSIER — la liasse imprimable. PAS SERVI, et l'onglet le dit.
 *
 * ⚠️ Mesuré le 24/09/2026 : aucun fichier du frontend ne porte le motif
 * « liasse », et aucun module du service non plus. Ce n'est donc pas un écran à
 * brancher, c'est un lot à écrire — item **D.993**.
 */
function OngletDossier() {
  return (
    <section className="avertissement">
      <strong>La liasse n'est pas encore produite.</strong>
      <p className="mention">
        La maquette prévoit ici le dossier imprimable : cocher les pièces, obtenir un
        bordereau qui dit ce qui est remis et qui l'a émis. Mesuré le 24/09/2026 :
        <strong> aucune route ni aucun écran</strong> ne porte la liasse — item D.993.
      </p>
      <p className="mention">
        Cet onglet n'est pas vide par oubli. Un panneau muet se lirait « rien à remettre »,
        alors qu'il faut lire « le geste n'existe pas encore ». Les pièces de l'affaire,
        elles, sont consultables et statuables dans l'onglet <em>Instruction</em>.
      </p>
    </section>
  );
}

/**
 * FRAUDE — les douze vecteurs. PAS SERVI, et l'onglet dit pourquoi.
 *
 * ⚠️ Six modules du service portent le motif « fraude », et AUCUN écran ne
 * l'expose. La maquette exige plus qu'une liste de risques : pour chacun, dire
 * **si le dispositif qui l'empêche est réellement en place**. C'est la seule des
 * trois colonnes qui compte — un dispositif déclaré et vide fait pire que rien,
 * il donne confiance. Afficher les douze vecteurs sans cette mesure serait
 * exactement le défaut que la maquette dénonce.
 */
function OngletFraude() {
  return (
    <section className="avertissement">
      <strong>Les douze vecteurs ne sont pas encore mesurés.</strong>
      <p className="mention">
        Six modules du service portent déjà des contrôles anti-fraude, et aucun écran ne
        les restitue. La maquette demande, pour chaque vecteur : comment on s'y prend,
        ce qui l'empêche, et <strong>si ce qui l'empêche est réellement en place</strong>.
      </p>
      <p className="mention">
        Seule la troisième colonne compte, et elle demande de mesurer douze dispositifs
        un par un. Les afficher sans cette mesure donnerait une confiance qu'on ne peut
        pas justifier — c'est précisément le défaut que la maquette dénonce.
        Les alertes de l'affaire restent visibles dans l'onglet <em>Instruction</em>.
      </p>
    </section>
  );
}
